/**
 * Suggested Tasks — displays smart task intelligence (auto-generated tasks
 * from CQRS events like payment failures, no-shows, stalled deals).
 * Wired to GET /crm/intelligence/tasks.
 */
import { Link } from "react-router";
import { useState } from "react";
import { Sparkles, AlertTriangle, ArrowRight } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useTaskIntelligence } from "@/hooks/use-task-intelligence";
import { formatMonthDay } from "@/lib/i18n";
import type { IntelligenceTask } from "@/hooks/use-task-intelligence";
import { getCrmTaskDefinition } from "@ease/shared/crm-task-registry";
import {
  buildCrmTaskActionGroups,
  buildCrmTaskContactChannelActionGroup,
  type CrmTaskAction,
  type CrmTaskActionGroup,
} from "@ease/crm/task-action-groups";
import {
  buildCrmTaskProfileSections,
  summarizeCrmTaskProfileSections,
  type CrmTaskProfileSection,
  type CrmTaskProfileSectionTone,
} from "@ease/crm/task-profile-sections";
import { shouldOpenCrmActionHrefInNewTab } from "@ease/crm/safe-action-hrefs";

const PRIORITY_COLOR: Record<string, string> = {
  urgent: "text-[var(--color-destructive)]",
  high: "text-[var(--color-accent-action)]",
  medium: "text-[var(--color-warning)]",
  low: "text-[var(--color-muted-foreground)]",
};

const ACTION_TYPE_LABEL: Record<string, string> = {
  create_follow_up_task: "Follow-up task",
  log_activity: "Log activity",
  update_lifecycle_stage: "Update profile",
  update_company_profile: "Update company",
  update_follow_up_task: "Update task",
  open_sales_deal: "Open sales deal",
  update_sales_deal: "Update sales deal",
  advance_deal: "Advance deal",
  tag_contact: "Tag contact",
  remove_contact_tag: "Remove tag",
};

const READINESS_LABEL: Record<string, string> = {
  ready: "Ready",
  needs_human_review: "Review required",
  blocked_invalid_input: "Blocked",
  blocked_unknown_action: "Unknown action",
};

const READINESS_CLASS: Record<string, string> = {
  ready: "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  needs_human_review: "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  blocked_invalid_input: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  blocked_unknown_action: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
};

const SIGNAL_TONE_CLASS: Record<"default" | "success" | "warning" | "danger" | "info", string> = {
  default: "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]",
  success: "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  warning: "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  danger: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  info: "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 text-[var(--color-primary)]",
};

const TASK_ACTION_TONE_CLASS: Record<CrmTaskAction["tone"], string> = {
  neutral: "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
  primary: "border-[var(--color-primary)]/25 bg-[var(--color-primary)]/5 text-[var(--color-primary)]",
  success: "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  warning: "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  danger: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
};

interface SuggestedTasksProps {
  contractRef?: string;
  signalFamily?: string;
  sourceEventType?: string;
  onAccepted?: (task: IntelligenceTask) => void;
}

function formatRate(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function prettySignalLabel(value: string): string {
  return value
    .replace(/^crm\./, "")
    .replace(/@v\d+$/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function suggestedTaskTypeLabel(type: string): string {
  const definition = getCrmTaskDefinition(type);
  if (definition.type !== "custom" || type === "custom") {
    return definition.label;
  }
  return ACTION_TYPE_LABEL[type] ?? prettySignalLabel(type);
}

function formatMetadataValue(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const values = value.map(formatMetadataValue).filter(Boolean);
    return values.length > 0 ? values.join(", ") : null;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const namedValue = formatMetadataValue(record.label)
      ?? formatMetadataValue(record.name)
      ?? formatMetadataValue(record.title);
    if (namedValue) return namedValue;

    const pairs = Object.entries(record)
      .map(([key, item]) => {
        const formatted = typeof item === "string" ? prettySignalLabel(item) : formatMetadataValue(item);
        return formatted ? `${prettySignalLabel(key)}: ${formatted}` : null;
      })
      .filter(Boolean);

    return pairs.length > 0 ? pairs.join("; ") : null;
  }
  return null;
}

function compactDetailRows(
  rows: Array<{ label: string; value: unknown }>,
): Array<{ label: string; value: string }> {
  return rows
    .map((row) => ({ label: row.label, value: formatMetadataValue(row.value) }))
    .filter((row): row is { label: string; value: string } => Boolean(row.value));
}

function suggestedInputRows(input: Record<string, unknown> | null | undefined): Array<{ label: string; value: string }> {
  if (!input) return [];
  return Object.entries(input)
    .map(([key, value]) => ({ label: prettySignalLabel(key), value: formatMetadataValue(value) }))
    .filter((row): row is { label: string; value: string } => Boolean(row.value))
    .slice(0, 8);
}

function isPersistedTask(task: IntelligenceTask): boolean {
  return !task.id.startsWith("event-action:");
}

function suggestedTaskContextSignal(task: IntelligenceTask): {
  label: string;
  tone: keyof typeof SIGNAL_TONE_CLASS;
  missing: string[];
} | null {
  const completeness = task.operatorReadModel?.contextCompleteness;
  if (!completeness) return null;
  return {
    label: completeness.isUsable ? "Operator-ready context" : "Needs operator context",
    tone: completeness.isUsable ? "success" : "warning",
    missing: completeness.missing,
  };
}

function formatSlaMinutes(value: number | null): string | null {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  const absoluteMinutes = Math.abs(value);
  if (absoluteMinutes < 60) return `${absoluteMinutes}m`;
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

function suggestedTaskSlaSignal(task: IntelligenceTask): {
  label: string;
  tone: keyof typeof SIGNAL_TONE_CLASS;
} | null {
  const sla = task.operatorReadModel?.sla;
  if (!sla || sla.status === "unscheduled") return null;
  const delta = formatSlaMinutes(sla.minutesUntilDue);
  const label = delta
    ? `${sla.label} (${sla.minutesUntilDue != null && sla.minutesUntilDue < 0 ? `${delta} late` : `${delta} left`})`
    : sla.label;
  return {
    label,
    tone: sla.severity === "critical" ? "danger" : sla.severity === "warning" ? "warning" : "info",
  };
}

function suggestedTaskCompletionLabel(task: IntelligenceTask): string | null {
  const schema = task.operatorReadModel?.completionSchema;
  if (!schema) return null;
  const outcomeCount = schema.outcomes.length;
  const fieldCount = schema.fields.length;
  return `${prettySignalLabel(schema.category)} completion: ${outcomeCount} ${outcomeCount === 1 ? "outcome" : "outcomes"}, ${fieldCount} ${fieldCount === 1 ? "field" : "fields"}`;
}

function taskEvidenceToneClass(tone: CrmTaskProfileSectionTone): string {
  if (tone === "neutral") return SIGNAL_TONE_CLASS.default;
  return SIGNAL_TONE_CLASS[tone];
}

function suggestedTaskQuickActions(groups: readonly CrmTaskActionGroup[]): CrmTaskAction[] {
  return groups
    .flatMap((group) => group.actions)
    .filter((action) => !action.disabled)
    .slice(0, 3);
}

function operatorBriefRows(task: IntelligenceTask): Array<{ label: string; value: string }> {
  return compactDetailRows((task.operatorReadModel?.operatorBrief ?? []).map((row) => ({
    label: row.label,
    value: row.value,
  }))).slice(0, 8);
}

type ReviewTargetEntityType = "person" | "deal" | "company" | "task" | "activity";

function stringField(record: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = record?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function reviewTargetForTask(task: IntelligenceTask): {
  targetEntityType: ReviewTargetEntityType;
  targetEntityId: string;
  contactId?: string | null;
} | null {
  const suggestedInput = task.metadata?.suggestedInput;
  const contactId = task.personId ?? stringField(suggestedInput, "personId");
  if (contactId) {
    return { targetEntityType: "person", targetEntityId: contactId, contactId };
  }

  const dealId = task.dealId ?? stringField(suggestedInput, "dealId");
  if (dealId) {
    return { targetEntityType: "deal", targetEntityId: dealId };
  }

  const companyId = stringField(suggestedInput, "companyId");
  if (companyId) {
    return { targetEntityType: "company", targetEntityId: companyId };
  }

  const taskId = stringField(suggestedInput, "taskId") ?? task.id;
  if (taskId) {
    return { targetEntityType: "task", targetEntityId: taskId };
  }

  const activityId = stringField(suggestedInput, "activityId");
  return activityId ? { targetEntityType: "activity", targetEntityId: activityId } : null;
}

function TaskCard({
  task,
  onReview,
  isReviewing,
}: {
  task: IntelligenceTask;
  onReview(task: IntelligenceTask, decision: "accepted" | "dismissed"): void;
  isReviewing: boolean;
}) {
  const priorityClass = PRIORITY_COLOR[task.priority] ?? PRIORITY_COLOR.medium;
  const typeLabel = task.operatorReadModel?.labels.type ?? suggestedTaskTypeLabel(task.type);
  const priorityLabel = task.operatorReadModel?.labels.priority ?? task.priority;
  const readiness = task.metadata?.readiness;
  const readinessLabel = readiness ? READINESS_LABEL[readiness.status] ?? readiness.status : null;
  const readinessClass = readiness ? READINESS_CLASS[readiness.status] ?? READINESS_CLASS.needs_human_review : null;
  const confidence = typeof task.metadata?.confidence === "number"
    ? Math.round(task.metadata.confidence * 100)
    : null;
  const adjustedConfidence = typeof task.metadata?.outcomeScoring?.adjustedConfidence === "number"
    ? Math.round(task.metadata.outcomeScoring.adjustedConfidence * 100)
    : null;
  const observedOutcome = task.metadata?.observedOutcome;
  const sourceEventType = task.metadata?.sourceEventType ?? task.triggerEventType ?? null;
  const signalFamily = task.metadata?.signalFamily ?? null;
  const canReview = Boolean(reviewTargetForTask(task) && task.metadata?.recommendationId && task.metadata?.actionRef && task.metadata?.intent);
  const [expanded, setExpanded] = useState(false);
  const contextSignal = suggestedTaskContextSignal(task);
  const slaSignal = suggestedTaskSlaSignal(task);
  const completionLabel = suggestedTaskCompletionLabel(task);
  const operatorGuidance = task.operatorReadModel?.guidance ?? task.operatorReadModel?.contextSummary ?? null;
  const operatorRows = operatorBriefRows(task);
  const suggestedRows = suggestedInputRows(task.metadata?.suggestedInput);
  const taskEvidenceRecords = [task.metadata, task.contextSnapshot];
  const taskProfileSections = buildCrmTaskProfileSections(taskEvidenceRecords, { maxSections: 4, maxItemsPerSection: 6 });
  const taskProfileSummary = summarizeCrmTaskProfileSections(taskProfileSections);
  const taskActionGroups = [
    buildCrmTaskContactChannelActionGroup(taskEvidenceRecords, { maxActions: 4 }),
    ...buildCrmTaskActionGroups(taskEvidenceRecords, { maxGroups: 4, maxActionsPerGroup: 5 }),
  ].filter((group): group is CrmTaskActionGroup => Boolean(group));
  const taskQuickActions = suggestedTaskQuickActions(taskActionGroups);
  const evidenceRows = compactDetailRows([
    { label: "Recommendation", value: task.metadata?.recommendationId },
    { label: "Action contract", value: task.metadata?.actionRef },
    { label: "Intent", value: task.metadata?.intent },
    { label: "Source event", value: sourceEventType },
    { label: "Source event ID", value: task.metadata?.sourceEventId },
    { label: "Signal family", value: signalFamily },
    { label: "Confidence", value: confidence == null ? null : `${confidence}%` },
    { label: "Score delta", value: task.metadata?.scoreDelta },
    { label: "Readiness", value: readiness?.status },
    { label: "Execution mode", value: task.metadata?.executionPlan?.mode ?? task.metadata?.executionMode },
    { label: "Execution reason", value: task.metadata?.executionPlan?.reason },
  ]);
  const readinessIssue = readiness?.issues?.[0]?.message ?? null;
  const hasDetails = Boolean(
    task.metadata?.scoreExplanation
    || operatorGuidance
    || readinessIssue
    || evidenceRows.length > 0
    || operatorRows.length > 0
    || suggestedRows.length > 0
    || taskProfileSections.length > 0
    || taskActionGroups.length > 0
    || observedOutcome,
  );

  return (
    <div className="stagger-item rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3 transition-colors hover:border-[var(--color-primary-border)]">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[var(--color-foreground)] truncate">
            {task.title}
          </p>
          {task.description && (
            <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)] line-clamp-2">
              {task.description}
            </p>
          )}
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${priorityClass} flex-shrink-0`}>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-current" />
          {priorityLabel}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
        <span className="rounded-full bg-[var(--color-ai-light)] px-2 py-0.5 text-[var(--color-ai)] border border-[var(--color-ai-border)]">
          {typeLabel}
        </span>
        {task.dueAt && (
          <span>
            Due {formatMonthDay(task.dueAt)}
          </span>
        )}
        {task.personId && (
          <Link
            to={`/crm/contacts/${task.personId}`}
            className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-0.5"
          >
            View contact <ArrowRight className="h-3 w-3" />
          </Link>
        )}
        {task.dealId && (
          <Link
            to={`/crm/deals/${task.dealId}`}
            className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-0.5"
          >
            View deal <ArrowRight className="h-3 w-3" />
          </Link>
        )}
        {isPersistedTask(task) && (
          <Link
            to={`/crm/tasks/${encodeURIComponent(task.id)}`}
            className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-0.5"
          >
            Open task workspace <ArrowRight className="h-3 w-3" />
          </Link>
        )}
        {readinessLabel && readinessClass && (
          <span className={`rounded-full border px-2 py-0.5 ${readinessClass}`}>
            {readinessLabel}
          </span>
        )}
        {confidence != null && (
          <span>{confidence}% confidence</span>
        )}
        {signalFamily && (
          <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-muted-foreground)]">
            {prettySignalLabel(signalFamily)} signal
          </span>
        )}
        {sourceEventType && (
          <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-muted-foreground)]">
            {prettySignalLabel(sourceEventType)}
          </span>
        )}
        {adjustedConfidence != null && adjustedConfidence !== confidence && (
          <span className="rounded-full border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-2 py-0.5 text-[var(--color-success)]">
            {adjustedConfidence}% outcome-adjusted
          </span>
        )}
        {observedOutcome && observedOutcome.confidence !== "none" && (
          <span className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-0.5 text-[var(--color-primary)]">
            {formatRate(observedOutcome.materializationRate)} materialized - {observedOutcome.confidence}
            {observedOutcome.evidenceAgeDays == null ? "" : ` - ${observedOutcome.evidenceAgeDays}d old`}
          </span>
        )}
      </div>
      {(contextSignal || slaSignal || completionLabel) && (
        <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
          {contextSignal && (
            <span className={`rounded-full border px-2 py-0.5 ${SIGNAL_TONE_CLASS[contextSignal.tone]}`}>
              {contextSignal.label}
            </span>
          )}
          {slaSignal && (
            <span className={`rounded-full border px-2 py-0.5 ${SIGNAL_TONE_CLASS[slaSignal.tone]}`}>
              SLA: {slaSignal.label}
            </span>
          )}
          {completionLabel && (
            <span className={`rounded-full border px-2 py-0.5 ${SIGNAL_TONE_CLASS.default}`}>
              {completionLabel}
            </span>
          )}
        </div>
      )}
      {(taskProfileSummary || taskQuickActions.length > 0) && (
        <div className="mt-2 flex flex-wrap gap-2 text-[11px]" aria-label="Suggested task quick evidence">
          {taskProfileSummary && (
            <span className={`rounded-full border px-2 py-0.5 ${taskEvidenceToneClass(taskProfileSummary.tone)}`}>
              Evidence: {taskProfileSummary.primaryLabel} · {taskProfileSummary.primaryValue}
              {taskProfileSummary.attentionItemCount > 0 ? ` · ${taskProfileSummary.attentionItemCount} attention` : ""}
            </span>
          )}
          {taskQuickActions.length > 0 && (
            <span className="flex flex-wrap gap-1" aria-label="Suggested task quick actions">
              {taskQuickActions.map((action) => (
                <SuggestedTaskActionLink key={`quick:${action.key}:${action.href}`} action={action} compact />
              ))}
            </span>
          )}
        </div>
      )}
      {operatorGuidance && (
        <p className="mt-2 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-1 text-xs text-[var(--color-primary)]">
          {operatorGuidance}
        </p>
      )}
      {readiness?.issues?.[0] && (
        <p className="mt-2 text-xs text-[var(--color-destructive)]">
          {readiness.issues[0].message}
        </p>
      )}
      {hasDetails && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            className="rounded-md border border-[var(--color-border)] px-2.5 py-1 text-[11px] text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]"
            aria-expanded={expanded}
          >
            {expanded ? "Hide details" : "Open details"}
          </button>
        </div>
      )}
      {expanded && (
        <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <p className="font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                Why this is suggested
              </p>
              <p className="mt-1 text-[var(--color-foreground)]">
                {operatorGuidance
                  ?? task.metadata?.scoreExplanation
                  ?? task.description
                  ?? readinessIssue
                  ?? "This recommendation was produced from CRM event intelligence."}
              </p>
              {contextSignal && contextSignal.missing.length > 0 && (
                <p className="mt-2 rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-2 py-1 text-[var(--color-warning)]">
                  Missing operator evidence: {contextSignal.missing.map(prettySignalLabel).join(", ")}
                </p>
              )}
              {readinessIssue && (
                <p className="mt-2 rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-2 py-1 text-[var(--color-warning)]">
                  Readiness issue: {readinessIssue}
                </p>
              )}
            </div>
            <div>
              <p className="font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                Review evidence
              </p>
              <dl className="mt-1 grid grid-cols-[110px_1fr] gap-x-2 gap-y-1">
                {evidenceRows.slice(0, 12).map((row) => (
                  <div key={`${row.label}:${row.value}`} className="contents">
                    <dt className="text-[var(--color-muted-foreground)]">{row.label}</dt>
                    <dd className="break-words text-[var(--color-foreground)]">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
          {operatorRows.length > 0 && (
            <div className="mt-3">
              <p className="font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                Operator brief
              </p>
              <dl className="mt-1 grid grid-cols-[130px_1fr] gap-x-2 gap-y-1">
                {operatorRows.map((row) => (
                  <div key={`${row.label}:${row.value}`} className="contents">
                    <dt className="text-[var(--color-muted-foreground)]">{row.label}</dt>
                    <dd className="break-words text-[var(--color-foreground)]">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {taskProfileSections.length > 0 && (
            <SuggestedTaskEvidenceSections sections={taskProfileSections} />
          )}
          {taskActionGroups.length > 0 && (
            <SuggestedTaskActionGroups groups={taskActionGroups} />
          )}
          {suggestedRows.length > 0 && (
            <div className="mt-3">
              <p className="font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                Suggested action input
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {suggestedRows.map((row) => (
                  <span
                    key={`${row.label}:${row.value}`}
                    className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1 text-[var(--color-muted-foreground)]"
                  >
                    <span className="font-medium text-[var(--color-foreground)]">{row.label}:</span> {row.value}
                  </span>
                ))}
              </div>
            </div>
          )}
          {observedOutcome && observedOutcome.confidence !== "none" && (
            <p className="mt-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-1 text-[var(--color-primary)]">
              Outcome evidence: {formatRate(observedOutcome.materializationRate)} materialized from prior accepted recommendations.
            </p>
          )}
        </div>
      )}
      {canReview && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onReview(task, "accepted")}
            disabled={isReviewing}
            className="rounded-md border border-[var(--color-success)]/30 px-2.5 py-1 text-[11px] text-[var(--color-success)] transition-colors hover:bg-[var(--color-success)]/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isReviewing ? "Recording..." : "Accept"}
          </button>
          <button
            type="button"
            onClick={() => onReview(task, "dismissed")}
            disabled={isReviewing}
            className="rounded-md border border-[var(--color-border)] px-2.5 py-1 text-[11px] text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-muted)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

function SuggestedTaskEvidenceSections({ sections }: { sections: CrmTaskProfileSection[] }) {
  return (
    <div className="mt-3">
      <p className="font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        Configured task evidence
      </p>
      <div className="mt-2 grid gap-2 md:grid-cols-2">
        {sections.slice(0, 4).map((section) => (
          <div key={section.key} className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-2">
            <p className="font-medium text-[var(--color-foreground)]">{section.label}</p>
            {section.description ? (
              <p className="mt-0.5 text-[11px] text-[var(--color-muted-foreground)]">{section.description}</p>
            ) : null}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {section.items.slice(0, 4).map((item) => (
                <span key={item.key} className={`rounded-full border px-2 py-0.5 ${taskEvidenceToneClass(item.tone)}`}>
                  <span className="font-medium">{item.label}:</span> {item.value}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SuggestedTaskActionGroups({ groups }: { groups: CrmTaskActionGroup[] }) {
  return (
    <div className="mt-3">
      <p className="font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        Task actions
      </p>
      <div className="mt-2 grid gap-2 md:grid-cols-2">
        {groups.slice(0, 4).map((group) => (
          <div key={group.key} className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-2">
            <p className="font-medium text-[var(--color-foreground)]">{group.label}</p>
            {group.description ? (
              <p className="mt-0.5 text-[11px] text-[var(--color-muted-foreground)]">{group.description}</p>
            ) : null}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {group.actions.slice(0, 5).map((action) => (
                <SuggestedTaskActionLink key={`${group.key}:${action.key}:${action.href}`} action={action} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SuggestedTaskActionLink({ action, compact = false }: { action: CrmTaskAction; compact?: boolean }) {
  const className = [
    "inline-flex items-center rounded-full border font-medium transition-colors",
    compact ? "px-2 py-0.5 text-[11px]" : "px-2 py-1 text-[11px]",
    TASK_ACTION_TONE_CLASS[action.tone] ?? TASK_ACTION_TONE_CLASS.neutral,
    action.disabled ? "cursor-not-allowed opacity-60" : "",
  ].filter(Boolean).join(" ");

  if (action.disabled) {
    return (
      <span className={className} title={action.disabledReason ?? action.description ?? undefined} aria-disabled="true">
        {action.label}
        {action.disabledReason ? <span className="ml-1 text-[var(--color-muted-foreground)]">({action.disabledReason})</span> : null}
      </span>
    );
  }

  if (action.href.startsWith("/") && !action.href.startsWith("//")) {
    return (
      <Link to={action.href} className={className} title={action.description ?? undefined}>
        {action.label}
      </Link>
    );
  }

  const opensNewTab = shouldOpenCrmActionHrefInNewTab(action.href);
  return (
    <a
      href={action.href}
      className={className}
      title={action.description ?? undefined}
      target={opensNewTab ? "_blank" : undefined}
      rel={opensNewTab ? "noreferrer" : undefined}
    >
      {action.label}
    </a>
  );
}

function taskMatchesScope(task: IntelligenceTask, filters: SuggestedTasksProps): boolean {
  if (filters.contractRef && task.metadata?.actionRef !== filters.contractRef) {
    return false;
  }

  if (filters.signalFamily && task.metadata?.signalFamily !== filters.signalFamily) {
    return false;
  }

  if (filters.sourceEventType) {
    const taskSourceEventType = task.metadata?.sourceEventType ?? task.triggerEventType;
    if (taskSourceEventType !== filters.sourceEventType) {
      return false;
    }
  }

  return true;
}

export function SuggestedTasks({
  contractRef,
  signalFamily,
  sourceEventType,
  onAccepted,
}: SuggestedTasksProps = {}) {
  const { data, isLoading, error, refetch, reviewActionRecommendation } = useTaskIntelligence();
  const [reviewingTaskId, setReviewingTaskId] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const handleReview = async (task: IntelligenceTask, decision: "accepted" | "dismissed") => {
    const metadata = task.metadata;
    const target = reviewTargetForTask(task);
    if (!target || !metadata?.recommendationId || !metadata.actionRef || !metadata.intent) {
      return;
    }

    setReviewingTaskId(task.id);
    setReviewError(null);

    try {
      await reviewActionRecommendation({
        contactId: target.contactId,
        targetEntityType: target.targetEntityType,
        targetEntityId: target.targetEntityId,
        recommendationId: metadata.recommendationId,
        actionRef: metadata.actionRef,
        intent: metadata.intent,
        decision,
        priority: task.priority,
        reason: task.description ?? undefined,
        requiresHumanReview: metadata.readiness?.requiresHumanReview,
        readinessStatus: metadata.readiness?.status,
        executionPlanStatus: metadata.executionPlan?.mode,
        executionPlanMode: metadata.executionPlan?.mode,
        executionPlanReason: metadata.executionPlan?.reason,
        confidence: metadata.confidence,
        sourceEventId: metadata.sourceEventId,
        sourceEventType: metadata.sourceEventType,
        signalFamily: metadata.signalFamily,
        sourceSurface: "crm.suggested_tasks",
        outcomeScoring: metadata.outcomeScoring,
        suggestedInput: metadata.suggestedInput,
      });
      await refetch();
      if (decision === "accepted") {
        onAccepted?.(task);
      }
    } catch (caughtError) {
      setReviewError(caughtError instanceof Error ? caughtError.message : "Failed to record action decision");
    } finally {
      setReviewingTaskId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonCard key={i} className="h-20" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        {error}
      </div>
    );
  }

  const filters = { contractRef, signalFamily, sourceEventType };
  const isScoped = Boolean(contractRef || signalFamily || sourceEventType);
  const openTasks = (data?.open ?? []).filter((task) => taskMatchesScope(task, filters));
  const escalatedTasks = (data?.escalated ?? []).filter((task) => taskMatchesScope(task, filters));
  const hasContent = openTasks.length > 0 || escalatedTasks.length > 0;

  if (!hasContent && !isScoped) return null;

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-5 w-5 text-[var(--color-ai)]" />
        <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Suggested Tasks</h2>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--color-ai-light)] text-[var(--color-ai)] border border-[var(--color-ai-border)]">
          Smart
        </span>
      </div>
      <p className="text-sm text-[var(--color-muted-foreground)] mb-4">
        Auto-generated from CRM events. Review and action these to keep your pipeline healthy.
      </p>
      {isScoped && (
        <p className="mb-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
          Suggested tasks scoped to {[
            contractRef ? prettySignalLabel(contractRef) : null,
            signalFamily ? `${prettySignalLabel(signalFamily)} signal` : null,
            sourceEventType ? prettySignalLabel(sourceEventType) : null,
          ].filter(Boolean).join(" · ")}
        </p>
      )}
      {reviewError && (
        <p className="mb-3 rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-xs text-[var(--color-destructive)]">
          {reviewError}
        </p>
      )}

      {!hasContent && isScoped && (
        <p className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm text-[var(--color-muted-foreground)]">
          No suggested tasks for this action scope yet. Use the event bridge preview or queue recommendations to create scoped review candidates.
        </p>
      )}

      {/* Escalated tasks first */}
      {escalatedTasks.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="h-3.5 w-3.5 text-[var(--color-destructive)]" />
            <span className="text-xs font-semibold text-[var(--color-destructive)]">
              Needs Attention ({escalatedTasks.length})
            </span>
          </div>
          <div className="space-y-2">
            {escalatedTasks.slice(0, 5).map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onReview={handleReview}
                isReviewing={reviewingTaskId === task.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Open suggested tasks */}
      {openTasks.length > 0 && (
        <div>
          <span className="text-xs font-semibold text-[var(--color-muted-foreground)] mb-2 block">
            Open ({openTasks.length})
          </span>
          <div className="space-y-2">
            {openTasks.slice(0, 8).map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onReview={handleReview}
                isReviewing={reviewingTaskId === task.id}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
